import * as React from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { m as motion, useReducedMotion } from "framer-motion";

import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/lib/auth";
import { ApiError, type UserRole, API_URL } from "@/lib/api";
import { TurnstileField } from "@/components/TurnstileField";
import { useDeviceFingerprint } from "@/hooks/useDeviceFingerprint";

/** Map Entra callback ?entra_error= codes to login-page copy. */
function messageForEntraError(code: string | null): string {
  switch ((code || "").trim().toLowerCase()) {
    case "not_authorized":
    case "not_in_group":
      return (
        "Your Microsoft account is not authorized for this application. " +
        "You must be in a Punchclock Entra security group (employees, subcontractors, or admins). " +
        "External vendors without Entra access should use Vendor Sign In with email and password."
      );
    case "role_conflict_privileged":
      return (
        "Your Microsoft account is in both the subcontractor group and an admin/manager/read-only admin group. " +
        "Those memberships cannot be combined. Contact IT to fix your group assignments."
      );
    case "role_conflict_employee":
      return (
        "Your Microsoft account is in both the subcontractor group and the Synergy employees group. " +
        "Those memberships cannot be combined. Contact IT to fix your group assignments."
      );
    case "contact_required":
      return (
        "Microsoft sign-in as a subcontractor could not link your Joiners Contact record. " +
        "Try again later or contact support."
      );
    case "tenant":
      return "Your Microsoft organization (tenant) is not allowed for this application.";
    case "inactive":
      return "Your account is inactive. Contact support if you need access.";
    default:
      return code
        ? "Microsoft sign-in could not be completed. Try again or use Vendor Sign In if you are an external vendor."
        : "";
  }
}

export function AuthPage({ mode }: { mode: "login" | "register" }) {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const prefersReducedMotion = useReducedMotion();

  const [authForm, setAuthForm] = React.useState({
    email: "",
    password: "",
    fullName: "",
    companyName: "",
    role: "employee" as UserRole,
    error: "",
    submitting: false,
    turnstileToken: "",
    turnstileResetKey: 0,
  });
  const { email, password, fullName, companyName, role, error, submitting, turnstileToken, turnstileResetKey } = authForm;
  const updateAuth = (p: Partial<typeof authForm>) => setAuthForm((f) => ({ ...f, ...p }));
  const setEmail = (v: string) => updateAuth({ email: v });
  const setPassword = (v: string) => updateAuth({ password: v });
  const setFullName = (v: string) => updateAuth({ fullName: v });
  const setCompanyName = (v: string) => updateAuth({ companyName: v });
  const setRole = (v: UserRole) => updateAuth({ role: v });
  const setError = (v: string) => updateAuth({ error: v });
  const setSubmitting = (v: boolean) => updateAuth({ submitting: v });
  const setTurnstileToken = (v: string) => updateAuth({ turnstileToken: v });
  const setTurnstileResetKey = (fn: number | ((k: number) => number)) => updateAuth({ turnstileResetKey: typeof fn === "function" ? fn(turnstileResetKey) : fn });
  const fp = useDeviceFingerprint();

  // Choice vs vendor password form for the /login page.
  // Employees should pick "Employee Sign In" (Entra). Vendors/subcontractors use the password form.
  const [loginView, setLoginView] = React.useState<'choice' | 'vendor'>('choice');
  const goToVendor = () => setLoginView('vendor');
  const backToChoice = () => {
    setLoginView('choice');
    // Keep any entered email for convenience if they return; clear only transient error.
    setError('');
  };

  // Surface Entra callback denials (?entra_error=not_authorized) on the login card.
  React.useEffect(() => {
    if (mode !== "login") return;
    const code = searchParams.get("entra_error");
    if (!code) return;
    const msg = messageForEntraError(code);
    if (msg) setError(msg);
    // Drop the query so refresh/share doesn't keep the banner forever.
    const next = new URLSearchParams(searchParams);
    next.delete("entra_error");
    setSearchParams(next, { replace: true });
  }, [mode, searchParams, setSearchParams]);

  // If we have a valid session (e.g. after Entra full redirect, or a just-completed
  // password login once the "me" query settles), get off the login page.
  // Skip the auto-redirect if we just came from an explicit logout (the state is
  // passed by the logout handler).
  const fromLogout = !!location.state?.fromLogout;

  // Use a ref to ensure we only trigger the redirect once per mount, even if the
  // effect re-runs multiple times while auth query / other effects settle.
  // This prevents chains of navigate() calls that lead to max update depth (React #185).
  const hasNavigatedRef = React.useRef(false);

  React.useEffect(() => {
    if (mode === "login" && auth.user && !auth.loading && !fromLogout && !hasNavigatedRef.current) {
      // Keep user on login if Entra just denied them (even if a stale cookie exists).
      if (error) return;
      hasNavigatedRef.current = true;
      navigate("/", { replace: true });
    }
  }, [auth.user, auth.loading, mode, navigate, fromLogout, error]);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const meta = fp.meta;
      const dfp = fp.fp || undefined;
      if (mode === "login") {
        await auth.login(email, password, turnstileToken, dfp, meta);
      } else {
        await auth.register({
          email,
          password,
          full_name: fullName,
          company_name: companyName,
          role,
          turnstile_token: turnstileToken,
          device_fingerprint: dfp,
          client_metadata: meta,
        });
      }
      navigate("/");
    } catch (err: any) {
      setError(err instanceof ApiError ? err.message : "Authentication failed.");
      // Allow the user to retry with a *fresh* Turnstile token (prevents
      // "timeout-or-duplicate" on the next submit after a creds or rate-limit failure).
      setTurnstileToken("");
      setTurnstileResetKey((k) => k + 1);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="grid min-h-screen place-items-center px-4 py-8">
      <motion.section
        className="grid w-full max-w-5xl gap-6 lg:grid-cols-[1fr_420px]"
        initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: prefersReducedMotion ? 0 : 0.25 }}
      >
        <div className="flex flex-col justify-center gap-5">
          <div className="flex items-center gap-3">
            <img src="/images/Synergy_Logo.png" alt="Synergy Fiber" className="h-10" />
            <div>
              <h1 className="text-l font-semibold tracking-normal">Dispatch Time Tracking</h1>
            </div>
          </div>
          <p className="max-w-xl text-sm leading-6 text-muted-foreground">
            Clock in with job context and protected reporting in one dashboard.
          </p>
          <div className="grid gap-3 sm:grid-cols-3">
            {["GPS required", "Photo capture", "Admin export"].map((item) => (
              <div key={item} className="rounded-lg border bg-card p-4 text-sm shadow-sm">
                <ShieldCheck className="mb-3 size-5 text-secondary" />
                {item}
              </div>
            ))}
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>{mode === "login" ? "Sign In" : "Create Account"}</CardTitle>
            <CardDescription>
              {mode === "login"
                ? (loginView === 'choice'
                    ? "Select sign-in method. Synergy Fiber employees should use Employee Sign In."
                    : "Use your email and password.")
                : "Create the first dashboard account."}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {error && (
              <Alert className="border-destructive/20 bg-destructive/10 text-destructive mb-4">
                <div className="mb-1 font-medium leading-none">Could not continue</div>
                <div className="text-sm">{error}</div>
              </Alert>
            )}

            {mode === "login" && loginView === 'choice' ? (
              <div className="grid gap-3" data-testid="login-page">
                <Button type="button" className="w-full" onClick={goToVendor} data-testid="vendor-signin-btn">
                  <span className="flex flex-col items-center">
                    <span>Vendor Sign In</span>
                    {/* <span className="text-xs font-normal opacity-80">Use username &amp; password</span> */}
                  </span>
                </Button>
                <Button
                  type="button"
                  className="w-full"
                  data-testid="employee-signin-btn"
                  onClick={() => { window.location.href = `${API_URL}/auth/entra/login`; }}
                >
                  <span className="flex flex-col items-center">
                    <span>Employee Sign In</span>
                    {/* <span className="text-xs font-normal opacity-80">Synergy Fiber via Microsoft Entra</span> */}
                  </span>
                </Button>

                <Link className="text-center text-sm font-medium text-primary" to="/vendor-signup">
                  Vendor signup
                </Link>
                <p className="text-center text-xs text-muted-foreground">
                  By using this service, you agree to our{" "}
                  <Link className="underline" to="/terms-of-service">Terms of Service</Link>{" "}
                  and{" "}
                  <Link className="underline" to="/privacy-policy">Privacy Policy</Link>.
                </p>
              </div>
            ) : (
              <form className="grid gap-4" onSubmit={handleSubmit}>
                {mode === "register" && (
                  <>
                    <div className="grid gap-2">
                      <Label htmlFor="full_name">Full name</Label>
                      <Input id="full_name" value={fullName} onChange={(event) => setFullName(event.target.value)} required />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="company_name">Company name</Label>
                      <Input id="company_name" value={companyName} onChange={(event) => setCompanyName(event.target.value)} required />
                    </div>
                    <div className="grid gap-2">
                      <div className="text-sm font-medium leading-none">Role</div>
                      <Select value={role} onValueChange={(value) => setRole(value as UserRole)}>
                        <SelectTrigger>
                          <SelectValue />
                          <SelectContent>
                            <SelectItem value="employee">Employee</SelectItem>
                            <SelectItem value="subcontractor">Subcontractor</SelectItem>
                            <SelectItem value="admin">Admin</SelectItem>
                          </SelectContent>
                        </SelectTrigger>
                      </Select>
                    </div>
                  </>
                )}

                <div className="grid gap-2">
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" data-testid="login-email" type="text" inputMode="email" value={email} onChange={(event) => setEmail(event.target.value)} required />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="password">Password</Label>
                  <Input id="password" data-testid="login-password" type="password" value={password} onChange={(event) => setPassword(event.target.value)} minLength={8} required autoComplete={mode === "login" ? "current-password" : "new-password"} />
                </div>

                <TurnstileField value={turnstileToken} onChange={setTurnstileToken} resetKey={turnstileResetKey} />

                <Button type="submit" data-testid="login-submit" disabled={submitting || !turnstileToken}>
                  {submitting ? "Working..." : mode === "login" ? "Sign In" : "Create Account"}
                </Button>

                {mode === "login" && (
                  <div className="text-center text-sm">
                    <Link className="font-medium text-primary" to="/forgot-password">Forgot password?</Link>
                  </div>
                )}

                {/* <div className="text-center text-sm text-muted-foreground">
                  {mode === "login" ? (
                    <>
                      Need an account? <Link className="font-medium text-primary" to="/register">Register</Link>
                    </>
                  ) : (
                    <>
                      Already registered? <Link className="font-medium text-primary" to="/login">Sign in</Link>
                    </>
                  )}
                </div> */}

                {/* Back link only relevant for the vendor password view on login */}
                {mode === "login" && loginView === 'vendor' && (
                  <button
                    type="button"
                    onClick={backToChoice}
                    className="text-center text-sm font-medium text-primary underline"
                  >
                    ← Back to sign-in options
                  </button>
                )}

                <Link className="text-center text-sm font-medium text-primary" to="/vendor-signup">
                  Vendor signup
                </Link>
                <p className="text-center text-xs text-muted-foreground">
                  By using this service, you agree to our{" "}
                  <Link className="underline" to="/terms-of-service">Terms of Service</Link>{" "}
                  and{" "}
                  <Link className="underline" to="/privacy-policy">Privacy Policy</Link>.
                </p>
              </form>
            )}
          </CardContent>
        </Card>
      </motion.section>
    </main>
  );
}
